import path from 'node:path'
import { getPresentUsers } from '@epic-web/workshop-presence/presence.server'
import { getApps } from '@epic-web/workshop-utils/apps.server'
import { getWorkshopConfig } from '@epic-web/workshop-utils/config.server'
import {
	getLoggedInProductHosts,
	getPreferences,
	getMutedNotifications,
} from '@epic-web/workshop-utils/db.server'
import { getEnv } from '@epic-web/workshop-utils/env.server'
import {
	getProgress,
	getUserInfo,
	userHasAccessToWorkshop,
} from '@epic-web/workshop-utils/epic-api.server'
import {
	checkForUpdatesCached,
	checkForExerciseChanges,
} from '@epic-web/workshop-utils/git.server'
import { getUnmutedNotifications } from '@epic-web/workshop-utils/notifications.server'
import { getOfflineVideoPlaybackIds } from '@epic-web/workshop-utils/offline-videos.server'
import { makeTimings } from '@epic-web/workshop-utils/timing.server'
import {
	getSetClientIdCookieHeader,
	getUserId,
} from '@epic-web/workshop-utils/user.server'
import { checkConnection } from '@epic-web/workshop-utils/utils.server'

import * as React from 'react'
import {
	data,
	type LinksFunction,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useNavigation,
} from 'react-router'
import { promiseHash } from 'remix-utils/promise'
import { useSpinDelay } from 'spin-delay'
import { type Route } from './+types/root.tsx'
import { CommandPalette } from './components/command-palette'
import { Confetti } from './components/confetti'
import { GeneralErrorBoundary } from './components/error-boundary'
import { ExerciseWarningBanner } from './components/exercise-warning-banner'
import { KeyboardShortcutsDialog } from './components/keyboard-shortcuts-dialog'
import { EpicProgress } from './components/progress-bar'
import { EpicToaster } from './components/toaster'
import { TooltipProvider } from './components/ui/tooltip'
import { UpdateToast } from './components/update-repo'
import { Notifications } from './routes/admin+/notifications'
import { useTheme } from './routes/theme/index'
import { getTheme } from './routes/theme/theme-session.server'
import appStylesheetUrl from './styles/app.css?url'
import tailwindStylesheetUrl from './styles/tailwind.css?url'
import { ClientHintCheck, getHints } from './utils/client-hints'
import { getConfetti } from './utils/confetti.server'
import { cn, combineHeaders, getDomainUrl, useAltDown } from './utils/misc'
import { getPracticePastLessonData } from './utils/practice-past-lesson'
import { Presence } from './utils/presence'
import { getSentryUser } from './utils/sentry-user'
import { getSeoMetaTags } from './utils/seo'
import { getToast } from './utils/toast.server'

export const links: LinksFunction = () => {
	return [
		{ rel: 'stylesheet', href: '/neogrotesk-font.css' },
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,200;0,300;0,400;0,500;0,600;1,700&display=swap',
		},
		{ rel: 'stylesheet', href: tailwindStylesheetUrl },
		{ rel: 'stylesheet', href: appStylesheetUrl },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
	]
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
	if (!loaderData) return []

	return getSeoMetaTags({
		instructor: loaderData.instructor,
		title: loaderData.workshopTitle,
		description: loaderData.workshopSubtitle,
		requestInfo: loaderData.requestInfo,
	})
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('rootLoader')
	const workshopConfig = getWorkshopConfig()
	const requestUrl = new URL(request.url)
	const {
		title: workshopTitle,
		subtitle: workshopSubtitle,
		instructor,
	} = workshopConfig

	const theme = getTheme(request)
	const { confettiId, headers: confettiHeaders } = getConfetti(request)
	const { toast, headers: toastHeaders } = await getToast(request)
	const isOnlinePromise = checkConnection({ request, timings })

	const asyncStuff = await promiseHash({
		userId: getUserId({ request }),
		preferences: getPreferences(),
		progress: getProgress({ timings }).catch((e) => {
			console.error('Failed to get progress', e)
			const emptyProgress: Awaited<ReturnType<typeof getProgress>> = []
			return emptyProgress
		}),
		user: getUserInfo(),
		userHasAccess: userHasAccessToWorkshop({ request, timings }),
		apps: getApps({ request, timings }),
		repoUpdates: checkForUpdatesCached(),
		unmutedNotifications: getUnmutedNotifications(),
		exerciseChanges: checkForExerciseChanges(),
		loggedInProductHosts: getLoggedInProductHosts(),
		offlineVideoPlaybackIds: getOfflineVideoPlaybackIds(),
	})

	const presentUsers = await getPresentUsers({
		request,
		timings,
	})

	// Filter out repoUpdates if muted
	const mutedNotifications = await getMutedNotifications()
	let repoUpdates = asyncStuff.repoUpdates
	if (
		repoUpdates?.updateNotificationId &&
		mutedNotifications.includes(repoUpdates.updateNotificationId)
	) {
		repoUpdates = {
			...repoUpdates,
			updatesAvailable: false,
			repoUpdatesAvailable: false,
			dependenciesNeedInstall: false,
		}
	}

	const practicePastLesson = getPracticePastLessonData({
		progress: asyncStuff.progress,
		currentPath: requestUrl.pathname,
	})

	return data(
		{
			...asyncStuff,
			workshopConfig,
			workshopTitle,
			workshopSubtitle,
			instructor,
			apps: asyncStuff.apps.map(({ name, fullPath, relativePath }) => ({
				name,
				fullPath,
				relativePath,
			})),
			ENV: getEnv(),
			practicePastLesson,
			requestInfo: {
				protocol: requestUrl.protocol,
				hostname: requestUrl.hostname,
				port: requestUrl.port,
				origin: requestUrl.origin,
				domain: getDomainUrl(request),
				hints: getHints(request),
				path: requestUrl.pathname,
				session: { theme },
				separator: path.sep,
				online: await isOnlinePromise,
			},
			toast,
			confettiId,
			presence: {
				users: presentUsers,
			},
			repoUpdates,
			exerciseChanges: asyncStuff.exerciseChanges,
			offlineVideoPlaybackIds: asyncStuff.offlineVideoPlaybackIds,
		},
		{
			headers: combineHeaders(
				toastHeaders,
				confettiHeaders,
				{ 'Server-Timing': timings.toString() },
				asyncStuff.userId?.type === 'cookie.randomId'
					? { 'Set-Cookie': getSetClientIdCookieHeader(asyncStuff.userId.id) }
					: undefined,
			),
		},
	)
}

function Document({
	children,
	env = {},
	className,
	style,
}: {
	children: React.ReactNode
	env?: Record<string, unknown>
	className: string
	style?: React.CSSProperties
}) {
	return (
		<html lang="en" className={className} style={style}>
			<head>
				<ClientHintCheck />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
				<script
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
			</head>
			<body className="bg-background text-foreground scrollbar-thin scrollbar-thumb-scrollbar h-screen-safe">
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const navigation = useNavigation()
	const showSpinner = useSpinDelay(navigation.state !== 'idle', {
		delay: 400,
		minDuration: 200,
	})
	const altDown = useAltDown()
	const [showKeyboardShortcuts, setShowKeyboardShortcuts] =
		React.useState(false)
	const commandPaletteRootData = React.useMemo(
		() => ({
			user: data.user,
			userHasAccess: data.userHasAccess,
		}),
		[data.user, data.userHasAccess],
	)

	React.useEffect(() => {
		const handleToggle = () => {
			setShowKeyboardShortcuts((prev) => !prev)
		}
		window.addEventListener('toggle-keyboard-shortcuts', handleToggle)
		return () => {
			window.removeEventListener('toggle-keyboard-shortcuts', handleToggle)
		}
	}, [])

	React.useEffect(() => {
		if (!ENV.EPICSHOP_IS_PUBLISHED) return
		const sentryUser = getSentryUser({
			user: data.user,
			userId: data.userId,
		})
		void import('@sentry/react-router')
			.then((Sentry) => {
				if (sentryUser) {
					Sentry.setUser(sentryUser)
				} else {
					Sentry.setUser(null)
				}
			})
			.catch(() => {})
	}, [data.user, data.userId])

	const theme = useTheme()
	return (
		<Document
			style={
				data.preferences?.fontSize
					? { fontSize: `${data.preferences?.fontSize}px` }
					: {}
			}
			className={cn(
				'h-screen-safe antialiased',
				theme,
				{ 'cursor-progress': showSpinner },
				altDown ? 'alt-down' : null,
			)}
			env={data.ENV}
		>
			{data.exerciseChanges &&
				!data.preferences?.exerciseWarning?.dismissed && (
					<ExerciseWarningBanner />
				)}
			<Outlet />
			<Confetti id={data.confettiId} />
			<EpicToaster toast={data.toast} />
			<UpdateToast repoUpdates={data.repoUpdates} />
			<EpicProgress />
			<Notifications unmutedNotifications={data.unmutedNotifications} />
			<CommandPalette rootData={commandPaletteRootData} />
			<KeyboardShortcutsDialog
				open={showKeyboardShortcuts}
				onOpenChange={setShowKeyboardShortcuts}
			/>
		</Document>
	)
}

export default function AppWithProviders() {
	const { user } = useLoaderData<typeof loader>()
	return (
		<Presence user={user}>
			<TooltipProvider>
				<App />
			</TooltipProvider>
		</Presence>
	)
}

export function ErrorBoundary() {
	return (
		<Document className="h-screen-safe">
			<GeneralErrorBoundary />
		</Document>
	)
}
